/**
 * Company Access Utilities
 * Provides centralized functions for validating user access to companies
 * based on the user document's companies array
 */

import { logger } from '@/services/loggerService';

/**
 * Check if a user has access to a specific company
 * @param {Object} user - User object with companies array
 * @param {string} companyId - Company ID to check access for
 * @returns {boolean} True if user has access, false otherwise
 */
export function hasCompanyAccess(user, companyId) {
  if (!user || !companyId) {
    logger.debug('[CompanyAccess] Missing user or companyId', { 
      hasUser: !!user, 
      companyId 
    });
    return false;
  }

  // Check if user has companies array (new system)
  if (user.companies && Array.isArray(user.companies)) {
    const hasAccess = user.companies.some(company => {
      // Handle both string IDs and company objects
      if (typeof company === 'string') {
        return company === companyId;
      }
      if (typeof company === 'object' && company.companyId) {
        return company.companyId === companyId && (!company.status || company.status === 'active');
      }
      return false;
    });
    
    logger.debug('[CompanyAccess] Checked companies array', { 
      userId: user.id,
      companyId, 
      hasAccess,
      companiesCount: user.companies.length,
      companies: user.companies.map(c => typeof c === 'string' ? c : c.companyId)
    });
    
    return hasAccess;
  }

  // Legacy fallback: check single companyId field
  if (user.companyId) {
    const hasAccess = user.companyId === companyId;
    logger.debug('[CompanyAccess] Checked legacy companyId', { 
      userId: user.id,
      companyId, 
      hasAccess,
      userCompanyId: user.companyId
    });
    return hasAccess;
  }
  
  // Check company field (older legacy format)
  if (user.company && !user.companyId) {
    const hasAccess = user.company === companyId;
    logger.debug('[CompanyAccess] Checked legacy company field', { 
      userId: user.id,
      companyId, 
      hasAccess,
      userCompany: user.company
    });
    return hasAccess;
  }

  // If no company data is available, this might be a data loading issue
  logger.warn('[CompanyAccess] No company access data found - user data may not be fully loaded', { 
    userId: user.id,
    companyId,
    userType: user.type,
    hasCompanies: !!user.companies,
    hasCompanyId: !!user.companyId,
    userKeys: Object.keys(user)
  });
  return false;
}

/**
 * Get all company IDs that a user has access to
 * @param {Object} user - User object with companies array
 * @returns {Array<string>} Array of company IDs
 */
export function getUserCompanyIds(user) {
  if (!user) {
    return [];
  }

  // Check if user has companies array (new system)
  if (user.companies && Array.isArray(user.companies)) {
    return user.companies
      .filter(company => {
        // Handle both string IDs and company objects
        if (typeof company === 'string') return true;
        if (typeof company === 'object' && company.companyId) {
          return !company.status || company.status === 'active';
        }
        return false;
      })
      .map(company => {
        return typeof company === 'string' ? company : company.companyId;
      });
  }

  // Legacy fallback: return single companyId
  if (user.companyId) {
    return [user.companyId];
  }

  return [];
}

/**
 * Get the user's primary/default company ID
 * @param {Object} user - User object with companies array
 * @returns {string|null} Primary company ID or null
 */
export function getPrimaryCompanyId(user) {
  if (!user) {
    return null;
  }

  // Check if user has companies array (new system)
  if (user.companies && Array.isArray(user.companies)) {
    // Look for primary company
    const primaryCompany = user.companies.find(company => {
      if (typeof company === 'object' && company.isPrimary && (!company.status || company.status === 'active')) {
        return true;
      }
      return false;
    });
    
    if (primaryCompany) {
      return primaryCompany.companyId;
    }
    
    // Fallback to first active company
    const firstActiveCompany = user.companies.find(company => {
      if (typeof company === 'string') return true;
      if (typeof company === 'object' && company.companyId) {
        return !company.status || company.status === 'active';
      }
      return false;
    });
    
    if (firstActiveCompany) {
      return typeof firstActiveCompany === 'string' ? firstActiveCompany : firstActiveCompany.companyId;
    }
  }

  // Legacy fallback: return single companyId
  if (user.companyId) {
    return user.companyId;
  }

  return null;
}

// Re-export isAdminUser from userUtils for backward compatibility